import React, { useContext, useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import * as Style from "./TabelaContratosStyle";
import {
  formatDateSystem,
  formatNumber,
  retornaResposta,
} from "../../assets/utils";
import { AuthContext } from "../../context/AuthContext";
import {
  FiDownload,
  FiExternalLink,
  FiCreditCard,
  FiChevronsLeft,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsRight,
  FiFileText,
  FiX,
  FiPrinter, // Adicionado ícone de impressora
} from "react-icons/fi";
import DepositInfoModal from "../Compra/DepositInfoModal/DepositInfoModal";
import PDFGenerator from "../Compra/CompraModal/PDFGenerator";
import PDFDownload from "./PDF";
import { formatDateDisplay } from "../../assets/assets";

const TabelaDeContratos = () => {
  const { contracts, userData } = useContext(AuthContext);
  const navigate = useNavigate();
  const [contratos, setContratos] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const [pageInput, setPageInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("Todos");
  const [showDepositModal, setShowDepositModal] = useState(false);

  // ESTADOS PARA O MODAL DE VISUALIZAÇÃO
  const [viewContractModal, setViewContractModal] = useState(false);
  const [selectedContractData, setSelectedContractData] = useState(null);

  const pdfRef = useRef();

  useEffect(() => {
    if (contracts) {
      setContratos(contracts);
    }
  }, [contracts]);

  const filteredContracts = useMemo(() => {
    return contratos
      .filter((item) => {
        if (statusFilter === "Todos") return true;
        return item.contract.status === parseInt(statusFilter, 10);
      })
      .filter((item) => {
        if (!searchTerm) return true;
        return item.contract.id
          .toString()
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      })
      .sort((a, b) => b.contract.id - a.contract.id);
  }, [contratos, searchTerm, statusFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const totalPages = Math.ceil(filteredContracts.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredContracts.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const [modalData, setModalData] = useState(null);

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handlePageInputChange = (e) => setPageInput(e.target.value);

  const handlePageInputSubmit = (e) => {
    e.preventDefault();
    const pageNumber = parseInt(pageInput);
    if (pageNumber && pageNumber > 0 && pageNumber <= totalPages) {
      paginate(pageNumber);
    }
    setPageInput("");
  };

  const handleOpenLink = (link) => window.open(link, "_blank");

  const shouldShowPaymentOptions = (status) => ![1, 2, 3, 5].includes(status);

  const handleRowClick = (contractId) => {
    navigate(`/contratos/${contractId}`);
  };

  // Preparar dados e abrir modal de visualização
  const handleViewContract = (contractData) => {
    const valorUnitario = contractData.totalPrice / contractData.contractQtt;

    const dataFormatada = {
      qttContratos: contractData.contractQtt,
      valorPorContrato: valorUnitario,
      ...contractData,
    };

    setSelectedContractData(dataFormatada);
    setViewContractModal(true);
  };

  const handleOpenDepsitModal = () => {
    setShowDepositModal(true)
  }

  const renderTicketCell = (contract) => {
    if (!shouldShowPaymentOptions(contract.status))
      return contract.status === 5 ? "RECOMPRADO" : "PAGO";
    if (contract.ticket) {
      return (
        <Style.ActionButton
          onClick={(e) => {
            e.stopPropagation();
            handleOpenLink(contract.ticket);
          }}
          title="Pagar Ticket"
        >
          <FiCreditCard />
        </Style.ActionButton>
      );
    }
    return (
      <Style.ActionButton
        onClick={(e) => {
          e.stopPropagation();
          handleOpenDepsitModal();
        }}
        title="Ver Conta para Depósito"
      >
        <FiExternalLink />
      </Style.ActionButton>
    );
  };



  // Função que dispara o download do PDF (JS-PDF)
  const handlePDFGenerator = (data) => {
    const newModalData = {
      assinatura: "assinado", // Adicione esta linha
      contract: {
        dias: 1095,
        lucroDiario: parseFloat(data.maxValorization) / 1095,
        porcentagemLucro: data.maxValorization,
        contractQtt: data.contractQtt,
        valorPorContrato:
          data.contractUniPrice || data.totalPrice / data.contractQtt,
        totalPrice: data.totalPrice,
        lucroTotal: data.totalPrice * 1.5,
        id: data.id,
        dateCreated: data.dateCreated,
      },
    };
    setModalData(newModalData);
  };

  useEffect(() => {
    if (modalData !== null) {
      pdfRef.current.downloadPDF();
    }
  }, [modalData]);

  return (
    <>
      <Style.Filters>
        <Style.FilterBox>
          <span className="name">Pesquisar por Código</span>
          <input
            placeholder="Digite o código do contrato..."
            className="input-box"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Style.FilterBox>
        <Style.FilterBox>
          <span className="name">Filtrar por Status</span>
          <select
            className="input-box"
            as="select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="Todos">Todos</option>
            <option value="1">Valorizando</option>
            <option value="2">Valorização Concluída</option>
            <option value="4">Pendente</option>
            <option value="3">Cancelado</option>
            <option value="5">Recomprado</option>
          </select>
        </Style.FilterBox>
      </Style.Filters>

      <Style.TabelaContainer>
        <Style.Tabela>
          <Style.TabelaHead>
            <Style.TabelaRow>
              <Style.TabelaHeader>Cód.</Style.TabelaHeader>
              <Style.TabelaHeader>Compra</Style.TabelaHeader>
              <Style.TabelaHeader>Término</Style.TabelaHeader>
              <Style.TabelaHeader>Qtd.</Style.TabelaHeader>
              <Style.TabelaHeader>Primeira Valorização</Style.TabelaHeader>
              <Style.TabelaHeader>Valor</Style.TabelaHeader>
              <Style.TabelaHeader>Valorização Atual</Style.TabelaHeader>
              <Style.TabelaHeader>Disp. Saque</Style.TabelaHeader>
              <Style.TabelaHeader>Valorização Final</Style.TabelaHeader>
              <Style.TabelaHeader>Contrato</Style.TabelaHeader>
              <Style.TabelaHeader>Pagamento</Style.TabelaHeader>
              <Style.TabelaHeader>Status</Style.TabelaHeader>
            </Style.TabelaRow>
          </Style.TabelaHead>
          <Style.TabelaBody>
            {currentItems.length === 0 ? (
              <Style.TabelaRow>
                <Style.TabelaData
                  colSpan="13"
                  style={{ textAlign: "center", padding: "3rem" }}
                >
                  {filteredContracts.length === 0 && contratos.length > 0
                    ? "Nenhum contrato encontrado com os filtros aplicados."
                    : "Ainda não há contratos para exibir."}
                </Style.TabelaData>
              </Style.TabelaRow>
            ) : (
              currentItems.map(({ contract, avaliableToWithdraw }) => (
                <Style.TabelaRow
                  key={contract.id}
                  onClick={() => handleRowClick(contract.id)}
                  isClickable
                  isGoldenBoxActived={contract.goldenBoxActived}
                >
                  <Style.TabelaData
                    isGoldenBoxActived={contract.goldenBoxActived}
                  >
                    {contract.id}
                  </Style.TabelaData>
                  <Style.TabelaData
                    isGoldenBoxActived={contract.goldenBoxActived}
                  >
                    {formatDateSystem(contract.dateCreated)}
                  </Style.TabelaData>
                  <Style.TabelaData
                    isGoldenBoxActived={contract.goldenBoxActived}
                  >
                    {formatDateSystem(contract.endContractDate)}
                  </Style.TabelaData>
                  <Style.TabelaData
                    isGoldenBoxActived={contract.goldenBoxActived}
                  >
                    {contract.contractQtt}
                  </Style.TabelaData>
                  <Style.TabelaData
                    isGoldenBoxActived={contract.goldenBoxActived}
                  >
                    {contract.firstValorization ? formatDateDisplay(contract.firstValorization) : " - "}
                  </Style.TabelaData>
                  <Style.TabelaData
                    isGoldenBoxActived={contract.goldenBoxActived}
                  >
                    R$ {formatNumber(contract.totalPrice)}
                  </Style.TabelaData>
                  <Style.TabelaData
                    isGoldenBoxActived={contract.goldenBoxActived}
                  >
                    {formatNumber(contract.actualValorization)}%
                  </Style.TabelaData>
                  <Style.TabelaData
                    isGoldenBoxActived={contract.goldenBoxActived}
                  >
                    <Style.HighlightText>
                      R${" "}
                      {contract.status === 3
                        ? formatNumber(0)
                        : formatNumber(avaliableToWithdraw)}
                    </Style.HighlightText>
                  </Style.TabelaData>
                  <Style.TabelaData
                    isGoldenBoxActived={contract.goldenBoxActived}
                  >
                    {formatNumber(contract.maxValorization)}%
                  </Style.TabelaData>

                  <Style.TabelaData
                    isGoldenBoxActived={contract.goldenBoxActived}
                  >
                    <Style.ActionButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewContract(contract);
                      }}
                      title="Visualizar Contrato Original"
                    >
                      <FiFileText />
                    </Style.ActionButton>
                  </Style.TabelaData>

                  <Style.TabelaData
                    isGoldenBoxActived={contract.goldenBoxActived}
                  >
                    {renderTicketCell(contract)}
                  </Style.TabelaData>
                  <Style.TabelaData
                    isGoldenBoxActived={contract.goldenBoxActived}
                  >
                    <Style.StatusBadge status={contract.status}>
                      {retornaResposta(contract.status)}
                    </Style.StatusBadge>
                  </Style.TabelaData>
                </Style.TabelaRow>
              ))
            )}
          </Style.TabelaBody>
        </Style.Tabela>
      </Style.TabelaContainer>

      {totalPages > 1 && (
        <Style.PaginationContainer>
          <Style.PaginationButton
            onClick={() => paginate(1)}
            disabled={currentPage === 1}
          >
            <FiChevronsLeft />
          </Style.PaginationButton>
          <Style.PaginationButton
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <FiChevronLeft />
          </Style.PaginationButton>
          <Style.PageInfo>
            Página {currentPage} de {totalPages}
          </Style.PageInfo>
          <form onSubmit={handlePageInputSubmit}>
            <Style.PageInput
              type="number"
              min="1"
              max={totalPages}
              value={pageInput}
              onChange={handlePageInputChange}
              placeholder="Ir"
            />
          </form>
          <Style.PaginationButton
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <FiChevronRight />
          </Style.PaginationButton>
          <Style.PaginationButton
            onClick={() => paginate(totalPages)}
            disabled={currentPage === totalPages}
          >
            <FiChevronsRight />
          </Style.PaginationButton>
        </Style.PaginationContainer>
      )}

      {showDepositModal && (
        <DepositInfoModal onClose={() => setShowDepositModal(false)} />
      )}

      {/* PDF Download invisível (Logica de Download JS-PDF) */}
      {modalData && <PDFDownload ref={pdfRef} modalData={modalData} />}

      {/* --- MODAL DE VISUALIZAÇÃO DO CONTRATO --- */}
      {viewContractModal && selectedContractData && (
        <Style.ModalOverlay onClick={() => setViewContractModal(false)}>
          <Style.ModalContent onClick={(e) => e.stopPropagation()}>
            <Style.ModalHeader>
              <h2>Contrato #{selectedContractData.id}</h2>

              <div
                style={{ display: "flex", alignItems: "center", gap: "15px" }}
              >
                {/* BOTÃO DE IMPRIMIR INTEGRADO */}
                <Style.PrintButton
                  onClick={() => handlePDFGenerator(selectedContractData)}
                  title="Baixar PDF do Contrato"
                >
                  <FiPrinter />
                  <span>Imprimir</span>
                </Style.PrintButton>

                <Style.CloseButton onClick={() => setViewContractModal(false)}>
                  <FiX />
                </Style.CloseButton>
              </div>
            </Style.ModalHeader>
            <Style.ModalBody>
              <PDFGenerator
                ContratoData={selectedContractData}
                assinatura="assinado"
                lastId={selectedContractData.id}
              />
            </Style.ModalBody>
          </Style.ModalContent>
        </Style.ModalOverlay>
      )}
    </>
  );
};

export default TabelaDeContratos;
